/**
 * Small pieces of the text section's inline "Edit" mode (FrameworkSection):
 *  - insertAtCursor: drop a symbol / `[ref:N]` marker at the caret of the
 *    focused input or textarea, going through React's onChange.
 *  - LineListEditor: objectives / takeaways as one-item-per-line text.
 *  - SubsectionTextEditor: a subsection's title + prose.
 */
import React from 'react';
import RichTextEditor from './richEditor/RichTextEditor';

const VALUE_SETTER = {
  INPUT: () => Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set,
  TEXTAREA: () => Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set,
};

/**
 * Inserts `text` at the caret of `el` (an <input>/<textarea>) and fires an
 * `input` event so React state updates. Returns false — doing nothing — when
 * `el` is not a text field. Uses the element's OWN prototype setter: calling
 * HTMLInputElement's setter on a <textarea> throws "Illegal invocation".
 */
export function insertAtCursor(el, text) {
  const getSetter = el && VALUE_SETTER[el.tagName];
  const setter = getSetter && getSetter();
  if (!setter) return false;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? start;
  setter.call(el, el.value.slice(0, start) + text + el.value.slice(end));
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.setSelectionRange(start + text.length, start + text.length);
  el.focus();
  return true;
}

/** Keeps focus in the text field when a toolbar-style button is clicked. */
export const keepFocus = (e) => e.preventDefault();

/** Items are kept line-for-line (blank lines included) while typing; the
 * caller drops blank lines on save. */
export function LineListEditor({ label, items, onChange, placeholder, testId }) {
  return (
    <div className="mt-5">
      <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
        {label} <span className="text-[10px] font-normal normal-case text-slate-400">(one per line)</span>
      </p>
      <textarea
        value={items.join('\n')}
        onChange={(e) => onChange(e.target.value.split('\n'))}
        placeholder={placeholder}
        rows={Math.max(3, items.length + 1)}
        className="w-full text-sm px-3 py-2 rounded-xl border border-zinc-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 resize-y"
        data-testid={testId}
      />
    </div>
  );
}

export function SubsectionTextEditor({ value, onChange, testId }) {
  return (
    <div className="mb-2" data-testid={testId}>
      <input
        value={value.title}
        onChange={(e) => onChange({ ...value, title: e.target.value })}
        placeholder="Subtitle"
        className="w-full text-sm font-bold text-gray-900 px-3 py-2 mb-2 rounded-lg border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#34A853]/30"
      />
      <div className="w-full border border-zinc-200 rounded-xl p-3">
        <RichTextEditor value={value.content} onChange={(content) => onChange({ ...value, content })} />
      </div>
    </div>
  );
}
